import '@stream-io/video-styling/dist/css/embedded.css';
import 'stream-chat-react/dist/css/v2/index.css';
import '../style/index.scss';
import { ComponentType } from 'react';
import { Session } from 'next-auth';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { SessionProvider } from 'next-auth/react';
import { StreamTheme } from '@stream-io/video-react-sdk';
import { SettingsProvider, useSettings } from '../context/SettingsContext';
import { AppEnvironmentProvider } from '../context/AppEnvironmentContext';
import { useEffect } from 'react';

const GoogleAnalytics = dynamic(
  () => import('@next/third-parties/google').then((mod) => mod.GoogleAnalytics),
  { ssr: false },
);
const GoogleTagManager = dynamic(
  () =>
    import('@next/third-parties/google').then((mod) => mod.GoogleTagManager),
  { ssr: false },
);

type AppProps = {
  Component: ComponentType;
  pageProps: {
    session: Session;
  };
};

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const GA_ID = process.env.NEXT_PUBLIC_GA_ID;

export default function App({
  Component,
  pageProps: { session, ...pageProps },
}: AppProps) {
  return (
    <SessionProvider
      session={session}
      basePath={`${process.env.NEXT_PUBLIC_BASE_PATH || ''}/api/auth`}
    >
      <Head>
        <title>EWTN Meet</title>
        <meta
          name="description"
          content="EWTN Meet — AI-powered video conferencing built for the global Catholic community. Seamlessly connect parishes, ministries, and the faithful across 160+ countries — rooted in the evangelizing mission of the world's largest Catholic media network."
        />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <AppEnvironmentProvider>
        <SettingsProvider>
          <AppContent Component={Component} pageProps={pageProps} />
        </SettingsProvider>
      </AppEnvironmentProvider>
      {GTM_ID && <GoogleTagManager gtmId={GTM_ID} />}
      {GA_ID && <GoogleAnalytics gaId={GA_ID} />}
    </SessionProvider>
  );
}

const AppContent = ({
  Component,
  pageProps,
}: {
  Component: ComponentType;
  pageProps: any;
}) => {
  const {
    settings: { theme },
  } = useSettings();

  useEffect(() => {
    document.body.classList.remove('dark', 'light');
    document.body.classList.add(theme);
  }, [theme]);

  return (
    <div className={`str-video__theme--${theme} ${theme}`}>
      <StreamTheme>
        <Component {...pageProps} />
      </StreamTheme>
    </div>
  );
};
